import { Repo } from "../apis/repo";
import { RequestData } from "../apis/repo_base";
import {
  SimulatedTransactionResponse,
  SimulateTransactionRequest,
} from "../types/brand";
import {
  OrderDetails,
  ProviderCustomData,
  RedemptionParams,
  RedemptionResponse,
} from "../types/order";
import { Optional } from "../../types";
import { PayOrderResponse } from "~/data/products";

const defaultMockDelayPromise = (ms: number = 1000) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, ms);
  });

// Track if profile has been updated to return mock data for v4/home
let isProfileUpdated = false;

export class TdMockRepo extends Repo {
  constructor(fetch: <T>(request: RequestData) => Promise<T>) {
    super(fetch);
  }

  async simulate(
    request: SimulateTransactionRequest
  ): Promise<SimulatedTransactionResponse> {
    await defaultMockDelayPromise(500);
    return {
      nextCursor: null,
      data: [
        {
          paymentSource: "PG_AND_WALLET_AND_COINS",
          coinToInrConversionRatio: 1,
          totalRequiredCoins: 450.0,
          totalRedeemableCoins: 0.0,
          planType: "BASIC",
          redemptionAmount: 500,
          requiredDeposit: 450.0,
          dueAmount: 450.0,
          discountDetails: {
            storeRewardType: "PREPAID",
            totalDiscount: {
              amount: 50.0,
              percentage: 10.0,
            },
            hubbleBaseDiscount: {
              amount: 50.0,
              percentage: 10.0,
            },
            hubbleWalletDiscount: {
              amount: 0,
              percentage: 0.0,
            },
            couponDiscount: {
              amount: 0,
              percentage: 0.0,
            },
            referralDiscount: {
              amount: 0,
              percentage: 0.0,
            },
            coinDiscount: {
              amount: 0,
              percentage: 0.0,
            },
            code: undefined,
          },
          additionalCharges: {
            transactionFees: {
              amount: 0,
              percentage: 0.0,
            },
            tdsCharges: {
              amount: 0,
              percentage: 0.0,
            },
          },
          paymentRoute: "PG",
          paymentMode: "CARD",
          routeStatus: "ACTIVE",
          denominationDetails: [
            {
              denomination: 500,
              quantity: 1,
            },
          ],
        },
      ],
    };
  }

  async initiateRedemption(
    params: RedemptionParams,
    headers?: Record<string, string>
  ): Promise<RedemptionResponse> {
    const path = "v2/redemptions/provider/pg/initiate";
    await defaultMockDelayPromise();
    return {
      id: "01JYEAN9CC0136T2V4GMWM9N7K",
      paymentRoute: "PG",
      planType: "BASIC",
      amount: 1000,
      depositPaymentDetails: {
        id: "01JYEAN9WQACPBJ2S3N3Y8ATHJ",
        amount: 0.0,
        status: "INITIATED",
        provider: "",
        otpToken: "",
        providerMetadata: {
          customerId: "",
          providerOrderId: "",
        },
      },
      storeOrderDetails: {
        id: "01JYEAN9NC3E6PJW86NNA7BQKX",
        amount: 1000,
        status: "PROCESSING",
      },
      refundDetails: {
        id: "",
        amount: 0.0,
        status: "INITIATED",
      },
    };
  }

  async getStoreOrderDetails(
    orderId: string,
    headers?: Record<string, string>
  ): Promise<OrderDetails> {
    await defaultMockDelayPromise();
    return {
      id: "01JYEAN9NC3E6PJW86NNA7BQKX",
      amount: 1000,
      date: "2025-06-23",
      status: "COMPLETED",
      orderProductDetails: {
        productType: "VOUCHER",
        productId: "01GMAVS2CHXR0XP1BZSTA9A44K",
        voucherProductTitle: "Amazon shopping",
        voucherProductLogoUrl:
          "https://assets.myhubble.money/brand-assets/logo-images-wp/amazon-logo.webp",
        voucherProductPlainLogoUrl:
          "https://assets.myhubble.money/brand-assets/wdmark-images-wp/amazon-wdmark.webp",
        voucherProductBackgroundColor: "#233448",
        voucherProductSummary: {
          totalVouchers: 1,
          totalAmount: 1000.0,
          totalBalance: 1000.0,
          activeVouchersCount: 1,
        },
        vouchers: [
          {
            id: "01JYEANJEEVV2ZYFQDSKKV8XTR",
            status: "ACTIVE",
            voucherType: "PIN_SECURED_CARD",
            cardNumber: "8090920019564144",
            cardPin: "217691",
            amount: 1000.0,
            balance: 1000.0,
            expiryDate: "2026-06-23",
            generatedDate: "2025-06-23",
            generatedDateTime: "2025-06-23T12:02:08.248421",
            voucherMetadata: {
              title: "Amazon shopping",
              cardImageUrl: "",
              tncUrl: "",
              tncResId: "voucher-tnc/amazon.md",
              heroImageUrl:
                "https://assets.myhubble.money/brand-assets/hero-images-wp/amazon-hero.webp",
              cardBackgroundColor: "#233448",
              howItWorksVideoUrl:
                "https://gullak-assets.s3.ap-south-1.amazonaws.com/brand-images/how-it-works-ss/Amazon_NEW_HTU.png",
              howItWorksCtaUrl: "",
              howItWorksStepsAvailable: false,
              canBalanceBeFetched: true,
              shareText:
                "Congratulations!\uD83C\uDF89\n\nYour friend has gifted\uD83C\uDF81 you a *Amazon shopping Brand card* worth *₹1000* using Hubble. Use this card number & PIN on your next purchase at Amazon shopping. \n\n✅ *Card Number:* 8090920019564144\n✅ *Card Pin:* 217691\n\nYou too can grab your favourite brand card on Hubble & get *up to 10% off* instantly!",
              addVoucherBrandPageUrl:
                "https://www.amazon.in/apay-products/apv/landing",
              addVoucherBrandPageImageUrl: null,
              voucherRedemptionType: "PIN_NO_SECURED",
              plainLogoUrl:
                "https://assets.myhubble.money/brand-assets/wdmark-images-wp/amazon-wdmark.webp",
              shareImageUrl:
                "https://gullak-assets.s3.ap-south-1.amazonaws.com/share-wa-brand-images/Amazon.jpg",
              howToUseDetailedStepsAvailable: true,
              isVoucherAssured: false,
            },
            brandId: "01GMAVS2CHXR0XP1BZSTA9A44K",
            orderId: "01JYEAN9NC3E6PJW86NNA7BQKX",
          },
        ],
        variantTitle: null,
        variantSubtext: null,
      },
      orderSavings: {
        usedRewardAmount: 0,
        discountAmount: 1000.0,
        totalCoinEarnings: 0,
      },
    };
  }

  async payOrderStandardCheckout(orderId: string): Promise<PayOrderResponse> {
    await defaultMockDelayPromise(1000);
    return {
      action: "PAY",
      provider: "RAZORPAY",
      providerOrderId: "01JYEAN9NC3E6PJW86NNA7BQKX",
      providerPaymentId: "01JYEAN9WQACPBJ2S3N3Y8ATHJ",
      link: "https://razorpay.com",
      statusUrl: "https://razorpay.com",
      returnUrl: "https://razorpay.com",
      providerCustomData: {} as ProviderCustomData,
      customerId: "td_test_customer_123",
    };
  }

  // Mock OTP initiation for phone update
  async initiatePhoneUpdate(
    phoneNumber: string
  ): Promise<{ otpToken: string }> {
    await defaultMockDelayPromise(800);
    return {
      otpToken: "td_mock_otp_token_123456",
    };
  }

  // Mock profile update
  async updatePartnerUserProfile(body: {
    firstName?: string;
    lastName?: string;
    email?: Optional<string>;
    phoneNumber?: Optional<string>;
  }): Promise<any> {
    await defaultMockDelayPromise(1000);

    // Set flag to return mock data for v4/home API
    isProfileUpdated = true;

    return {
      success: true,
      message: "Profile updated successfully",
      user: {
        firstName: body.firstName || "Test",
        lastName: body.lastName || "User",
        email: body.email || "test@example.com",
        phoneNumber: body.phoneNumber || "+919876543210",
      },
    };
  }

  // Mock verify method for OTP verification
  async verify(_otpToken: string, otp: string): Promise<any> {
    await defaultMockDelayPromise(600);
    if (otp === "123456") {
      return {
        success: true,
        message: "OTP verified successfully",
      };
    }
    throw new Error("Invalid OTP");
  }

  // Mock confirmPhoneUpdate method
  async confirmPhoneUpdate(otp: string, otpToken: string): Promise<any> {
    await defaultMockDelayPromise(800);
    if (otp === "123456" && otpToken === "td_mock_otp_token_123456") {
      return {
        success: true,
        message: "Phone number updated successfully",
        user: {
          phoneNumber: "+919876543210",
          phoneVerified: true,
        },
      };
    }
    throw new Error("Invalid OTP or token");
  }

  // Mock getPaymentOrderDetails for Razorpay order creation
  async getPaymentOrderDetails(orderId: string): Promise<any> {
    await defaultMockDelayPromise(800);
    return {
      id: orderId,
      providerOrderId: "td_razorpay_order_" + Date.now(),
      amount: 450, // 10% discount applied on 500
      currency: "INR",
      status: "CREATED",
      providerCustomData: {},
    };
  }

  // Reset mock state (useful for tests)
  static resetMockState(): void {
    isProfileUpdated = false;
  }
}
